import React, { useCallback, useMemo, useState } from 'react';
import { components } from 'react-select';
import AdvancedSelect from '../common/AdvancedSelect';
import { ABORT_ERROR, makeRESTApiRequest } from '../../helpers/api';
import { useDebouncedAbortableLoader } from '../HoaDirectory/useHoaDirectorySearch';

const HoaDirectoryOption = props => {
  const { data } = props;
  return (
    <components.Option {...props}>
      <div className="fw-bold midFont mb-0">{data.label}</div>
      {data.subtitle ? <div className="tinyFont text-muted">{data.subtitle}</div> : null}
    </components.Option>
  );
};

/**
 * Async creatable picker for HOA directory entities (Associations / Management Companies).
 */
const HoaDirectoryAutocomplete = ({
  value = '',
  selectedId = null,
  onSelect,
  onCreateNew,
  searchEndpoint,
  mapOption,
  placeholder = 'Type to search…',
  invalid = false,
  creatableLabel = 'Add',
}) => {
  const [items, setItems] = useState([]);

  const searchDirectory = useCallback(
    async (q, signal) => {
      const { response, error } = await makeRESTApiRequest({
        endpoint: searchEndpoint,
        method: 'POST',
        requestBody: { query: q, limit: 20 },
        signal,
      });

      if (error === ABORT_ERROR) return [];
      if (error || !Array.isArray(response)) return [];

      const opts = response.map(raw => {
        const mapped = mapOption(raw);
        return {
          label: mapped.label,
          value: String(mapped.id),
          subtitle: mapped.subtitle || '',
          raw,
        };
      });
      setItems(opts);
      return opts;
    },
    [searchEndpoint, mapOption]
  );

  const loadOptions = useDebouncedAbortableLoader(searchDirectory);

  const selectedItems = useMemo(() => {
    if (!value) return null;
    if (selectedId) {
      return {
        label: value,
        value: String(selectedId),
      };
    }
    return {
      label: value,
      value: value,
    };
  }, [value, selectedId]);

  const handleChange = option => {
    if (!option) {
      // When cleared (X icon), reset to blank without calling onCreateNew
      onSelect({ _id: null, name: '', phone: '', email: '', contacts: [] });
      return;
    }
    if (option.raw) {
      onSelect(option.raw);
      return;
    }
    onCreateNew(option.label || option.value || '');
  };

  return (
    <AdvancedSelect
      creatable
      searchable
      isMulti={false}
      isClearable
      height="31px"
      placeholder={placeholder}
      isInvalid={invalid}
      items={items}
      selectedItems={selectedItems}
      loadOptions={loadOptions}
      onChange={handleChange}
      onCreateOption={input => onCreateNew(String(input || '').trim())}
      CustomOptionComponent={HoaDirectoryOption}
      creatableLabel={creatableLabel}
      noOptionsMessage="No results found"
      useMenuPortal
    />
  );
};

export default HoaDirectoryAutocomplete;
